import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { clearAntetrekPopupToken, getAntetrekPopupTokenStart } from 'store/redux-slices/user'
import {
  ANTETREK_RETRY_DELAY_MS,
  connectAntetrek,
  disconnectAntetrek,
  getAntetrekRefreshDelay,
  isAntetrekPopupEnabled,
  isAntetrekTokenExpired,
  isAntetrekTokenStale,
  loadAntetrekScript,
  onAntetrekReady
} from 'utils/antetrek'

/**
 * Drives the AnteTrek realtime popup widget for the logged-in player: fetch a
 * connect-token once userId is known, inject the widget script and connect,
 * refresh the token before it expires, and tear down on logout.
 *
 * Keyed on `userDetails.userId` (never `loggedIn`): `loggedIn` flips from
 * localStorage before the profile arrives, see ZohoChatButtonControl.
 */
export default function useAntetrekPopup () {
  const dispatch = useDispatch()
  const userId = useSelector((state) => state.user.userDetails?.userId)
  const { enabled, token, expiresAt, failedAt } = useSelector((state) => state.user.antetrekPopup) || {}
  const active = isAntetrekPopupEnabled()
  const connectedTokenRef = useRef(null)
  const prevUserIdRef = useRef(null)

  // 1. Token per login/reload; teardown when userId disappears (logout / 403 reset)
  useEffect(() => {
    if (!active) return
    if (userId) {
      dispatch(getAntetrekPopupTokenStart())
    } else if (prevUserIdRef.current) {
      disconnectAntetrek()
      connectedTokenRef.current = null
      dispatch(clearAntetrekPopupToken())
    }
    prevUserIdRef.current = userId || null
  }, [active, userId, dispatch])

  // 2. Load the script and (re)connect once per token; a refreshed token replaces the old connection
  useEffect(() => {
    if (!active || !enabled || !token || connectedTokenRef.current === token) return
    loadAntetrekScript()
    return onAntetrekReady(() => {
      if (connectedTokenRef.current === token) return
      try {
        if (connectedTokenRef.current) disconnectAntetrek()
        connectAntetrek(token)
        connectedTokenRef.current = token
      } catch (error) {
        console.error('AnteTrek popup widget connect failed', error)
      }
    })
  }, [active, enabled, token])

  // 3. Proactive refresh before expiry. The refresh fires when the margin begins, so a failed
  //    refresh keeps retrying on a short cadence until the token has really expired.
  useEffect(() => {
    if (!active || !enabled || !token || !userId) return
    let delay
    if (failedAt) {
      if (isAntetrekTokenExpired(expiresAt)) return // nothing left to protect: the next reload/login recovers
      delay = ANTETREK_RETRY_DELAY_MS
    } else {
      delay = getAntetrekRefreshDelay(expiresAt)
      if (delay === null) return // no usable expiresAt: connect once, never refresh
    }
    const timer = setTimeout(() => dispatch(getAntetrekPopupTokenStart()), delay)
    return () => clearTimeout(timer)
  }, [active, enabled, token, expiresAt, failedAt, userId, dispatch])

  // 4. Background tabs throttle timers: refresh on return if the token went stale meanwhile
  useEffect(() => {
    if (!active || !enabled || !token || !userId) return
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAntetrekTokenStale(expiresAt)) {
        dispatch(getAntetrekPopupTokenStart())
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [active, enabled, token, expiresAt, userId, dispatch])
}
